import { formatPriceWithoutCurrency } from '@common/utils/price';
import { getEventMetaDescription, getEventUrl } from '@common/utils/programme';
import { CURRENCY, PRODUCTION_WEBSITE_URL } from '@data/constants';
import { SupabaseEventRecord } from '@interfaces';

interface ListingAdditionalMicrodataProps {
	event: SupabaseEventRecord;
	isWithDescription?: boolean;
	isWithArtistList?: boolean;
	isWithImage?: boolean;
	isWithOffer?: boolean;
	isWithUrl?: boolean;
}

export const ListingAdditionalMicrodata = ({
	event,
	isWithDescription = false,
	isWithArtistList = false,
	isWithImage = false,
	isWithOffer = false,
	isWithUrl = false,
}: ListingAdditionalMicrodataProps) => {
	const eventUrl = getEventUrl({ event });
	const eventDescription = getEventMetaDescription(event);

	return (
		<>
			<div
				itemProp="location"
				itemScope
				itemType="https://schema.org/MusicVenue"
			>
				<meta content="Elsewhere" itemProp="name" />
				{event.address ? (
					<meta content={event.address} itemProp="address" />
				) : null}
			</div>

			<div
				itemProp="organizer"
				itemScope
				itemType="https://schema.org/Organization"
			>
				<meta content="Elsewhere" itemProp="name" />
				<meta content={PRODUCTION_WEBSITE_URL} itemProp="url" />
			</div>

			<meta content={event.end_date} itemProp="endDate" />

			<meta
				content={
					event.status === 'cancelled'
						? 'https://schema.org/EventCancelled'
						: 'https://schema.org/EventScheduled'
				}
				itemProp="eventStatus"
			/>

			<meta
				content="https://schema.org/OfflineEventAttendanceMode"
				itemProp="eventAttendanceMode"
			/>

			{isWithDescription ? (
				<meta content={eventDescription} itemProp="description" />
			) : null}

			{isWithArtistList ? (
				<>
					{event.artists.map((name, index) => {
						const key = `${event.slug}-${index}`;
						return (
							<div
								itemProp="performer"
								itemScope
								itemType="https://schema.org/MusicGroup"
								key={key}
							>
								<meta content={name} itemProp="name" />
							</div>
						);
					})}
				</>
			) : null}

			{isWithImage && event.image_urls.length ? (
				<meta content={event.image_urls[0]} itemProp="image" />
			) : null}

			{isWithOffer ? (
				<div itemProp="offers" itemScope itemType="https://schema.org/Offer">
					{event.ticket_url ? (
						<link href={event.ticket_url} itemProp="url" />
					) : null}
					<link
						href={`https://schema.org/${
							event.sold_out ? `SoldOut` : `InStock`
						}`}
						itemProp="availability"
					/>
					{event.representative_ticket_price ? (
						<meta
							content={formatPriceWithoutCurrency({
								cents: event.representative_ticket_price,
							})}
							itemProp="price"
						/>
					) : null}
					<meta content={CURRENCY} itemProp="priceCurrency" />
					{event.sale_start_date ? (
						<meta content={event.sale_start_date} itemProp="validFrom" />
					) : null}
				</div>
			) : null}

			{isWithUrl ? <link href={eventUrl.href} itemProp="url" /> : null}
		</>
	);
};
